import adminConfig from '@/features/promotions/config.json';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { computed } from 'vue';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

// Tags that qualify a title to promote Monolith (tm1255573) via SQTV in AU
export const MONOLITH_TAGS = [
	'alien_encounter',
	'artists_and_showbiz',
	'boys_night',
	'buried_truth',
	'california',
	'captivating',
	'clever',
	'computers_and_web',
	'conspiracies',
	'cover_up',
	'critically_acclaimed',
	'cyberspace',
	'deception',
	'dishonesty',
	'documentary',
	'drama',
	'evidence',
	'fiction',
	'horror',
];

export const MONOLITH_TITLE_ID = 'tm1255573';
export const MONOLITH_SHORTNAME = 'jwt';

type MonolithTargeting = { provider: string; placement: 'sqtv' };

function titleHasMonolithTag(title: TitleDetail): boolean {
	return (title.content.tags ?? []).some(t => MONOLITH_TAGS.includes(t.technicalName));
}

// Shared by useSingleQTV to scope its jwEntityIDs filter - only checks the queried provider, not the title itself
export function isMonolithProviderActive(shortName: string | null, country: string): boolean {
	return (
		shortName === MONOLITH_SHORTNAME &&
		country === 'AU' &&
		!!adminConfig.steering_overrides_global.monolith_sqtv_targeting_au
	);
}

/*
 * Fronted Override - Monolith SQTV Override
 * For AU titles matching the Monolith tag set, force SQTV placement filtered to Monolith (tm1255573).
 */
export function getMonolithTargeting(title: TitleDetail, country: string): MonolithTargeting | null {
	if (!isMonolithProviderActive(MONOLITH_SHORTNAME, country)) return null;
	// exclude AVOD JWTV titles
	if (title.offers.some(o => o.package.shortName === MONOLITH_SHORTNAME && o.monetizationType === 'FREE'))
		return null;
	if (!titleHasMonolithTag(title)) return null;

	return { provider: MONOLITH_SHORTNAME, placement: 'sqtv' };
}

export function useMonolithTargeting(title: MaybeRefOrGetter<TitleDetail>, enabled: MaybeRefOrGetter<boolean> = true) {
	const { country } = storeToRefs(useLanguageStore());

	const _title = computed(() => toValue(title));
	// false when flag is off, country is not AU, or title doesn't match tags/offer exclusions
	const isMonolithActive = computed(() => !!getMonolithTargeting(_title.value, country.value) && toValue(enabled));
	return { isMonolithActive };
}
